import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';
import { BasketActions, BasketActionTypes, BasketLoadingItem, LoadingItemTypes } from '../basket.types';
import { ACTION_CREATOR_NAME, userHelper, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { AppState, ErrorAction } from '../../root.types';
import { api } from 'repositories/api';
import { BasketItemModel, ProductTypes, BasketDetailsModel, SOFASHIELD } from 'models/Basket';
import { initBasketPageSaga } from './initBasketPage';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

const DEFAULT_SOFASHIELD_COUNT = 0;

export interface RemoveSofashieldAction extends Action<BasketActionTypes.REMOVE_SOFASHIELD> {
    itemProductCode: string;
}

export interface RemoveSofashieldActionStart extends Action<BasketActionTypes.REMOVE_SOFASHIELD_START> {
    loadingItem: BasketLoadingItem;
}

export interface RemoveSofashieldActionSuccess extends Action<BasketActionTypes.REMOVE_SOFASHIELD_SUCCESS> {
    loadingItem: BasketLoadingItem;
}

export interface RemoveSofashieldActionError extends ErrorAction<BasketActionTypes.REMOVE_SOFASHIELD_ERROR> {
    loadingItem: BasketLoadingItem;
}

export const removeSofashield: (itemProductCode: string) => RemoveSofashieldAction =
    (itemProductCode: string) => ({
        type: BasketActionTypes.REMOVE_SOFASHIELD,
        itemProductCode
    });
removeSofashield[ACTION_CREATOR_NAME] = 'removeSofashield';

export type RemoveSofashieldFunc = (...args: Parameters<typeof removeSofashield>) => void;

export function* removeSofashieldSaga({ itemProductCode }: RemoveSofashieldAction) {
    yield put<BasketActions>({
        type: BasketActionTypes.REMOVE_SOFASHIELD_START,
        loadingItem: { id: itemProductCode, loadingType: LoadingItemTypes.RemoveSofashield }
    });
}

export function* removeSofashieldQueueSaga({ loadingItem }: RemoveSofashieldActionStart): BasketSagaReturnType {
    try {
        let state: AppState = yield select();

        const entryItem = state.basketState.basket!.entries?.find((entry: BasketItemModel) => (
            entry?.product.code === loadingItem.id
        ));

        if (entryItem?.entryNumber !== undefined) {
            yield api.hybris.updateSofashieldQuantity(
                userHelper.getCurrentUserName(),
                state.basketState.basket!.guid,
                entryItem.entryNumber,
                entryItem.quantity,
                DEFAULT_SOFASHIELD_COUNT
            );

            const sofashieldItem = state.basketState.basket!.entries.find(e => e.product.name === SOFASHIELD);

            yield callBasketSaga(initBasketPageSaga, {
                type: BasketActionTypes.INIT_BASKET_PAGE
            });

            state = yield select();
            const basket = state.basketState.basket!;

            sendAnalytics(sofashieldItem?.product.code ?? SOFASHIELD, entryItem.sofashieldQuantity, sofashieldItem, basket);
        }

        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_SOFASHIELD_SUCCESS,
            loadingItem
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.REMOVE_SOFASHIELD_ERROR,
            loadingItem,
            error: errorToBasketErrorModel(error, 'removeSofashieldQueueSaga')
        });

        return false;
    }
}

function sendAnalytics(code: string, removedQuantity: number, sofashieldItem: BasketItemModel | undefined, updatedBasket: BasketDetailsModel): void {
    analyticsHelper.withLogging(() => {
        api.google.removeAdditionalProduct(ProductTypes.Sofashield, code, removedQuantity);
        api.exponea.removeFromCart(updatedBasket, sofashieldItem!, removedQuantity);
    }, 'removeSofashieldQueueSaga');
}